import { BASE_PATHS, template } from '@belong/common';
import { IconCheck2 } from '@belong/icons';
import { Button, Link, Text } from '@belong/ui';
import { RESIDENT_PRE_QUALIFICATION_STRINGS as STRINGS } from '../resident-pre-qualification.strings';
import { ResidentPreQualificationModel } from '../types';
import { doesIncomeMeetThreshold, getRentIncomeMultiple } from '../utils';

function IconWarning() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true">
      <path
        d="M12 2.5a1.6 1.6 0 011.39.8l9.4 16.3a1.6 1.6 0 01-1.39 2.4H2.6a1.6 1.6 0 01-1.39-2.4l9.4-16.3a1.6 1.6 0 011.39-.8z"
        fill="currentColor"
      />
      <path
        d="M12 7.6a1.2 1.2 0 011.2 1.24l-.3 5.5a.9.9 0 01-1.8 0l-.3-5.5A1.2 1.2 0 0112 7.6zm0 8.6a1.3 1.3 0 110 2.6 1.3 1.3 0 010-2.6z"
        fill="#fff"
      />
    </svg>
  );
}

function ResultCriteria({
  isMet,
  children,
}: {
  isMet: boolean;
  children: string;
}) {
  return (
    <div className="flex items-center gap-2xs">
      <span className={isMet ? 'text-green' : 'text-orange'}>
        {isMet ? (
          <IconCheck2 width={16} height={16} className="shrink-0" />
        ) : (
          <IconWarning />
        )}
      </span>
      <Text
        variant="p1"
        fontWeight="semibold"
        className={isMet ? 'text-green' : 'text-orange'}
      >
        {children}
      </Text>
    </div>
  );
}

type Props = {
  preQualification: ResidentPreQualificationModel;
  ctaLabel: string;
  notQualifiedCtaLabel?: string;
  isProceeding?: boolean;
  onProceed: () => void;
};

export function PreQualificationResult({
  preQualification,
  ctaLabel,
  notQualifiedCtaLabel = STRINGS['result.browseOtherHomes'],
  isProceeding = false,
  onProceed,
}: Props) {
  const { estimatedOutcome, reportedCreditScoreBand } = preQualification;
  const rentIncomeMultiple = getRentIncomeMultiple(preQualification);

  return (
    <div className="w-full">
      <Text variant="h2" fontWeight="semibold" className="text-center">
        {STRINGS[`result.${estimatedOutcome}.title`]}
      </Text>
      <Text className="mt-sm text-center">
        {STRINGS[`result.${estimatedOutcome}.body`]}
      </Text>

      {estimatedOutcome === 'NotQualified' ? (
        <div className="mt-2xl flex justify-center">
          <Link
            buttonType="primary"
            variant="solid"
            href={BASE_PATHS.HOME}
            size="fluid"
            className="whitespace-nowrap md:w-auto md:px-xl"
          >
            {notQualifiedCtaLabel}
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-2xl flex flex-wrap items-center justify-center gap-lg">
            <ResultCriteria isMet={doesIncomeMeetThreshold(preQualification)}>
              {template(STRINGS['result.rentIncome'], {
                multiple: rentIncomeMultiple,
              })}
            </ResultCriteria>
            <ResultCriteria isMet={reportedCreditScoreBand !== 'Poor'}>
              {STRINGS[`result.credit.${reportedCreditScoreBand}`]}
            </ResultCriteria>
          </div>
          <div className="mt-2xl flex justify-center">
            <Button
              size="fluid"
              className="whitespace-nowrap md:w-auto md:px-xl"
              disabled={isProceeding}
              onClick={onProceed}
            >
              {ctaLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
